"use client";

import { ArrowRight, ChevronRight } from "lucide-react";
import { Comedian } from "@punchup/data/legacy/comedian";
import { ComedianAvatar } from "./ComedianAvatar";
import Image from "next/image";
import Link from "next/link";
import type { VenueShowGroup } from "../../data/venueGridItem";
import { cn } from "@punchup/utils/cn";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { tixologiEventImageUrl } from "./showPosterUrl";
import { getTextColorForBackground } from "@punchup/utils/colorContrast";
import { bodyHtmlToPlainTextClient } from "@punchup/utils/rich-text/plain-text-to-body-html";
import { ModalOrDrawer } from "@punchup/ui-common/components/common";
import { useMemo, useState } from "react";
import { useTextOverflow } from "../hooks/useTextOverflow";
import { useTixologiEvent } from "@punchup/data/legacy/hooks/tixologi";
import { useVenuePageTheme } from "../../hooks";
import { useVenueShowGroupPath } from "./show-groups/useVenueShowGroupHref";
import {
  formatVenueShowGroupDateBadge,
  formatVenueShowGroupDateRange,
} from "./venueShowGroupDate";

const MAX_AVATARS = 4;

/** Fallback accent for the CTA link when the card renders without a venue theme. */
const DEFAULT_ACCENT_COLOR = "#FF5E25";

export interface VenueShowGroupCardProps {
  group: VenueShowGroup;
  venueSlug: string;
  mobileView?: boolean;
  venueLogoImg: string | null;
  followedComedians?: string[];
  /** Theme: background for the image area when there's no poster. */
  cardBackgroundColor: string;
  /** Theme accent for the "See all N dates" link; defaults to the brand accent. */
  accentColor?: string;
}

/** Dedup comedians across all member shows, preserving lineup order. */
function useGroupComedians(group: VenueShowGroup): Comedian[] {
  return useMemo(() => {
    const seen = new Set<string>();
    const out: Comedian[] = [];
    for (const show of group.shows) {
      for (const c of show.show_comedians ?? []) {
        if (!c?.id || seen.has(c.id)) continue;
        seen.add(c.id);
        out.push(c);
      }
    }
    return out;
  }, [group.shows]);
}

function AvatarStack({
  comedians,
  followedComedians,
}: {
  comedians: Comedian[];
  followedComedians?: string[];
}) {
  return (
    <div className="flex -space-x-3">
      {comedians.slice(0, MAX_AVATARS).map((comedian, index) => (
        <div
          key={comedian.id}
          className="relative"
          style={{ zIndex: MAX_AVATARS - index }}
        >
          <div className="rounded-full border border-white">
            <ComedianAvatar
              comedianImgUrl={
                comedian.about_img
                  ? getCDNImageURL(comedian.about_img)
                  : undefined
              }
              comedianName={comedian.display_name || ""}
              size="xs"
              isFollowed={followedComedians?.includes(comedian.id)}
              isNotPunchupComedian={!comedian.is_punchup_verified}
            />
          </div>
          {index === MAX_AVATARS - 1 && comedians.length > MAX_AVATARS && (
            <div className="border border-white w-full h-full left-0 top-0 absolute bg-black/50 rounded-full flex items-center justify-center z-10">
              <span className="text-white text-xs font-regular leading-none mt-px">
                +{comedians.length - MAX_AVATARS}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A collapsed card representing a show-group (multiple shows that share a
 * `show_group_link` or an auto-resolved title). The whole card links to the
 * existing `/shows/{slug}` drill-down. Rendered on the venue grid only when the
 * venue's `shows_grouping_mode` is `grouped`.
 */
export default function VenueShowGroupCard({
  group,
  venueSlug,
  mobileView = false,
  venueLogoImg,
  followedComedians,
  cardBackgroundColor,
  accentColor = DEFAULT_ACCENT_COLOR,
}: VenueShowGroupCardProps) {
  const href = useVenueShowGroupPath(venueSlug, group.slug);
  const comedians = useGroupComedians(group);
  const dateRange = formatVenueShowGroupDateRange(
    group.startDatetime,
    group.endDatetime
  );
  const showCount = group.shows.length;
  const comedianNames = comedians
    .map((c) => c.display_name)
    .filter(Boolean)
    .join(", ");

  // Cover image: the cover show's Tixologi promo image (the same source the
  // individual cards use) so a "Rachel Feinstein" group shows the Rachel
  // artwork, not the venue logo. Falls back to a member poster, then the
  // headliner's photo, then the venue logo as a last resort.
  const coverShow = useMemo(
    () => group.shows.find((s) => s.tixologi_event_id) ?? group.shows[0],
    [group.shows]
  );
  const { data: tixologiEvent, isLoading: isTixologiLoading } =
    useTixologiEvent(coverShow?.tixologi_event_id ?? undefined);
  const cover = useMemo((): { url: string | null; isLogo: boolean } => {
    // While a ticketed cover show is still fetching its Tixologi art, hold a
    // neutral placeholder instead of flashing a poster/headliner fallback that
    // would then swap to the promo image — the individual card shows a
    // placeholder until its image resolves for the same reason.
    if (coverShow?.tixologi_event_id && isTixologiLoading) {
      return { url: null, isLogo: false };
    }
    const tixologiImage = coverShow?.tixologi_event_id
      ? tixologiEventImageUrl(tixologiEvent)
      : null;
    if (tixologiImage) {
      return { url: tixologiImage, isLogo: false };
    }
    const withPoster = group.shows.find((s) => s.poster_img);
    if (withPoster?.poster_img) {
      return { url: getCDNImageURL(withPoster.poster_img), isLogo: false };
    }
    const headliner = comedians[0];
    if (headliner?.about_img) {
      return { url: getCDNImageURL(headliner.about_img), isLogo: false };
    }
    return venueLogoImg
      ? { url: getCDNImageURL(venueLogoImg), isLogo: true }
      : { url: null, isLogo: false };
  }, [
    coverShow,
    tixologiEvent,
    isTixologiLoading,
    group.shows,
    comedians,
    venueLogoImg,
  ]);

  // Grouped shows almost always share one blurb; surfacing it gives the card the
  // same title → description → button anatomy as an individual card, which is
  // what keeps the grid rows aligned.
  // metadata_text may be rich HTML (PUN-5353); the card preview and its
  // whitespace-pre-wrap modal render it as plain text, so flatten it
  // client-side — a legacy `\n`-only row keeps its line breaks. Pick the first
  // show whose FLATTENED text is non-empty, so markup with no words never wins.
  const description =
    group.shows
      .map((s) => bodyHtmlToPlainTextClient(s.metadata_text ?? ""))
      .find(Boolean) ?? "";
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const { ref: descriptionRef, isOverflowing: isDescriptionOverflowing } =
    useTextOverflow(description);

  // Desktop list row. A min-height matching the individual row keeps grouped and
  // single rows aligned in list view (PUN-4601): the individual row is 96px —
  // its md:h-16 avatars (64px) plus p-4 (32px) — and those are fixed, so this
  // doesn't drift.
  if (!mobileView) {
    return (
      <Link
        href={href || "#"}
        className="group/card flex min-h-[96px] flex-row items-center gap-4 py-1"
      >
        <div className="w-[110px] shrink-0">
          <div className="text-sm font-bold text-primary-blue">{dateRange}</div>
          <div className="text-xs text-secondary-blue">
            {showCount} {showCount === 1 ? "show" : "shows"}
          </div>
        </div>
        {comedians.length > 0 && (
          <div className="shrink-0">
            <AvatarStack
              comedians={comedians}
              followedComedians={followedComedians}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="truncate text-lg font-bold text-primary-blue group-hover/card:underline">
            {group.title}
          </div>
          {comedianNames && (
            <div className="truncate text-sm text-secondary-blue">
              {comedianNames}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-row items-center gap-1 text-secondary-blue">
          <span className="hidden text-sm font-semibold md:inline">
            See all dates
          </span>
          <ChevronRight className="h-5 w-5" />
        </div>
      </Link>
    );
  }

  const dateBadge = formatVenueShowGroupDateBadge(
    group.startDatetime,
    group.endDatetime
  );
  // A light (white) badge reads on a dark backdrop and a dark badge on a light
  // one. Over a real cover photo we darken the top strip with a scrim so a light
  // badge always reads; with no photo we pick the variant from the themed card
  // background's luminance (the same helper the page theme uses for text).
  const overPhoto = !!cover.url && !cover.isLogo;
  const badgeOnDark =
    overPhoto || getTextColorForBackground(cardBackgroundColor) === "white";
  const badgeClasses = badgeOnDark
    ? "bg-white/95 text-primary-blue"
    : "bg-primary-blue text-white";

  // Mobile / grid card. The whole card navigates via a stretched <Link> overlay
  // rather than an anchor wrapping the content, so the interactive "Read more"
  // button is a sibling of the anchor — no interactive/tabindex descendant lives
  // inside an <a>. The content sits above the overlay with pointer-events-none so
  // clicks fall through to the link, except the button which re-enables them.
  return (
    <div className="group/card relative block h-full">
      <Link
        href={href || "#"}
        aria-label={group.title}
        className="absolute inset-0 z-0"
      />
      <div className="pointer-events-none relative z-10 flex h-full flex-col overflow-hidden rounded-[10px] shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)]">
        <div
          className="relative h-full flex-1 overflow-hidden"
          style={{ backgroundColor: cardBackgroundColor }}
        >
          {cover.url &&
            (cover.isLogo ? (
              <Image
                src={cover.url}
                alt={group.title}
                fill
                className="object-contain p-8"
                sizes="(max-width: 768px) 100vw, 327px"
              />
            ) : (
              // Blurred object-cover backdrop + object-contain foreground —
              // mirrors TourDateRow's MobileCardImage so a poster whose aspect
              // ratio doesn't match the card renders in full instead of being
              // center-cropped (PUN oncall F2). The foreground sits at z-10;
              // the badges below are bumped to z-20 so they stay on top.
              <>
                <div className="absolute inset-0 overflow-hidden">
                  <Image
                    src={cover.url}
                    alt=""
                    fill
                    className="object-cover blur-md scale-110"
                    sizes="(max-width: 768px) 100vw, 327px"
                  />
                </div>
                <div className="absolute inset-0 z-10 p-4">
                  <Image
                    src={cover.url}
                    alt={group.title}
                    fill
                    className="object-contain"
                    sizes="(max-width: 768px) 100vw, 327px"
                  />
                </div>
              </>
            ))}
          {overPhoto && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-20 bg-gradient-to-b from-black/40 to-transparent" />
          )}
          <div
            className={cn(
              "absolute left-3 top-3 z-20 flex flex-col rounded-lg px-2.5 py-1.5 shadow-sm",
              // Center the compact calendar-chip; left-align a multi-line span
              // so the range reads cleanly instead of ragging around center.
              "month" in dateBadge ? "items-center" : "items-start",
              badgeClasses
            )}
          >
            {"month" in dateBadge ? (
              <>
                <span className="text-[11px] font-semibold uppercase leading-none tracking-wide">
                  {dateBadge.month}
                </span>
                <span className="text-lg font-bold leading-tight">
                  {dateBadge.day}
                </span>
              </>
            ) : (
              <>
                <span className="text-[13px] font-bold uppercase leading-tight">
                  {dateBadge.start}
                </span>
                <span className="text-[13px] font-bold uppercase leading-tight">
                  {dateBadge.end}
                </span>
              </>
            )}
          </div>
          <div
            className={cn(
              "absolute right-3 top-3 z-20 flex items-center rounded-lg px-2.5 py-2.5 text-sm font-bold shadow-sm",
              badgeClasses
            )}
          >
            {showCount} {showCount === 1 ? "show" : "shows"}
          </div>
          {comedians.length > 0 && (
            <div className="absolute bottom-3 left-3 right-3 z-20 flex flex-row items-center gap-x-1.5 rounded-lg bg-black/40 px-2 py-0.5 backdrop-blur-3xl">
              <AvatarStack
                comedians={comedians}
                followedComedians={followedComedians}
              />
              <div className="flex-1 truncate text-left text-sm font-regular text-white">
                {comedianNames}
              </div>
            </div>
          )}
        </div>
        {/* Footer mirrors the individual card (title → description → CTA) at the
            same min height so both card types line up in the grid. The CTA is a
            bold, accent-colored "See all N dates" link with an arrow — it
            browses the series rather than starting a purchase, so it reads as a
            link, not a button. */}
        <div className="flex min-h-[158px] flex-col gap-2 px-4 pb-4 pt-3">
          <div className="line-clamp-2 text-lg font-bold text-primary-blue">
            {group.title}
          </div>
          <div className="flex-1">
            {description && (
              <div className="relative overflow-hidden text-sm font-light text-secondary-blue">
                <p
                  ref={descriptionRef}
                  className="overflow-hidden text-ellipsis line-clamp-2"
                >
                  {description}
                </p>
                {isDescriptionOverflowing && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDescriptionModalOpen(true);
                    }}
                    className="pointer-events-auto absolute bottom-0 right-0 inline-flex items-baseline"
                  >
                    <span className="w-8 bg-gradient-to-l from-white to-transparent">
                      &zwj;
                    </span>
                    <span className="bg-white font-semibold text-primary-blue">
                      ... Read more
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
          <div
            className="flex flex-row items-center gap-1.5 text-sm font-bold"
            style={{ color: accentColor }}
          >
            See all {showCount} dates
            <ArrowRight className="h-4 w-4 transition-transform group-hover/card:translate-x-0.5" />
          </div>
        </div>
      </div>
      {description && (
        <ModalOrDrawer
          isOpen={isDescriptionModalOpen}
          onOpenChange={setIsDescriptionModalOpen}
          className="z-[99] md:max-h-[75dvh] md:overflow-y-scroll"
        >
          <div className="flex flex-col gap-4 pb-4 pt-2">
            <h2 className="font-sans text-xl font-semibold normal-case text-primary-blue">
              {group.title}
            </h2>
            <p className="whitespace-pre-wrap break-words text-base leading-relaxed text-neutral-700">
              {description}
            </p>
          </div>
        </ModalOrDrawer>
      )}
    </div>
  );
}

interface VenueShowGroupCardThemedProps extends Omit<
  VenueShowGroupCardProps,
  "cardBackgroundColor"
> {
  venueSlug: string;
}

/** Theme wrapper mirroring {@link TourDateRowThemed}. */
export function VenueShowGroupCardThemed({
  venueSlug,
  ...rest
}: VenueShowGroupCardThemedProps) {
  const { headerBackgroundColor, accentColor } = useVenuePageTheme(venueSlug);
  return (
    <VenueShowGroupCard
      {...rest}
      venueSlug={venueSlug}
      cardBackgroundColor={headerBackgroundColor}
      accentColor={accentColor}
    />
  );
}
